import React from "react";
import CampaignResult from "../components/CampaignResult";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import "../styles/campaign.css";
import SkeletonCampaign from "../components/SkeletonCampaign";

const MyCampaigns = forwardRef(({ accessToken, pointsRef }, ref) => {

    const [runningCampaigns, setRunningCampaigns] = useState([]);
    const [completedCampaigns, setCompletedCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const formatDate = (isoDate) => {
        const date = new Date(isoDate);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options).replace(',', '');
    };

    const editCampaign = async (campaignId, updatedPoints) => {
        const { pointsFor30Sec, pointsForHalf, pointsForFull } = updatedPoints;

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACK4APP_SERVER_PATH}/api/campaigns/${campaignId}`,
                { pointsFor30Sec, pointsForHalf, pointsForFull },
                { params: { accessToken } }
            );
            fetchCampaigns();
            return response.data.message;
        } catch (err) {
            alert("Failed to update campaign: " + err.response?.data?.error || "Unknown error");
        }
    };

    const fetchCampaigns = async () => {
        setLoading(true);
        setError("");

        try {
            const response = await axios.get(`${process.env.REACT_APP_BACK4APP_SERVER_PATH}/api/my-campaigns`, {
                params: { accessToken },
            });

            const campaigns = response.data.campaigns;

            const running = [];
            const completed = [];

            campaigns.forEach((campaign) => {
                const campaignData = {
                    _id: campaign._id,
                    image: campaign.trackImage,
                    title: campaign.trackName,
                    author: campaign.trackArtist,
                    creationDate: formatDate(campaign.creationDate),
                    budgetPoints: campaign.budgetPoints,
                    spentPoints: campaign.spentPoints,
                    pointsFor30Sec: campaign.pointsFor30Sec,
                    pointsForHalf: campaign.pointsForHalf,
                    pointsForFull: campaign.pointsForFull,
                };

                if (campaign.budgetPoints > campaign.spentPoints) {
                    running.push(campaignData);
                } else {
                    completed.push(campaignData);
                }
            });

            setRunningCampaigns(running);
            setCompletedCampaigns(completed);
        } catch (err) {
            setError("Failed to fetch campaigns: " + err.response?.data?.error || "Unknown error");
        } finally {
            setLoading(false);
        }
    };

    const deleteCampaign = async (campaignId) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACK4APP_SERVER_PATH}/api/campaigns/${campaignId}`,
                { params: { accessToken } }
            );

            const refundPoints = response.data.refundPoints;

            fetchCampaigns();

            if (pointsRef.current) {
                pointsRef.current.refreshPoints();
            }
        } catch (err) {
            setError("Failed to delete campaign: " + err.response?.data?.error || "Unknown error");
        }
    };

    useImperativeHandle(ref, () => ({
        refreshCampaigns: fetchCampaigns,
    }));

    useEffect(() => {
        if (!accessToken) return;
        fetchCampaigns();
    }, [accessToken]);

    return (
        <div className="my-campaigns">
            {loading ? (
                <div>
                    <div className="campaign-columns">
                        <div className="campaign-column">
                            <h4>Running Campaigns</h4>
                            <div>
                                <SkeletonCampaign/>
                            </div>
                        </div>

                        <div className="campaign-column">
                            <h4>Completed Campaigns</h4>
                            <div>
                                <SkeletonCampaign/>
                            </div>
                        </div>
                    </div>
                </div>
            ) : error ? (
                <p style={{ color: "red" }}>{error}</p>
            ) : (
                <div>
                    <div className="campaign-columns">
                        <div className="campaign-column">
                            <h4>Running Campaigns</h4>
                            <div>
                                {runningCampaigns.length > 0 ? (
                                    runningCampaigns.slice().reverse().map((campaign, index) => (
                                        <CampaignResult
                                            key={index}
                                            campaign={campaign}
                                            onDelete={() => deleteCampaign(campaign._id)}
                                            onEdit={editCampaign}
                                        />
                                    ))
                                ) : (
                                    <p>No campaigns</p>
                                )}
                            </div>
                        </div>

                        <div className="campaign-column">
                            <h4>Completed Campaigns</h4>
                            <div>
                                {completedCampaigns.length > 0 ? (
                                    completedCampaigns.map((campaign, index) => (
                                        <CampaignResult
                                            key={index}
                                            campaign={campaign}
                                            onDelete={() => deleteCampaign(campaign._id)}
                                            onEdit={editCampaign}
                                        />
                                    ))
                                ) : (
                                    <p>No campaigns</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default MyCampaigns;